import axios from 'axios';
import { ROUTES } from '../constants/routes.constants';
import { API_ENDPOINTS } from '../constants/apiEndpoints.constants';

/**
 * Client API centralisé avec intercepteurs
 * Gère automatiquement :
 * - L'ajout du token d'authentification
 * - Le rafraîchissement automatique des tokens expirés
 * - La gestion centralisée des erreurs
 * - Les timeouts
 */

// Créer une instance axios avec la configuration de base
const apiClient = axios.create({
  baseURL: ROUTES.API_BASE_URL,
  timeout: 30000, // 30 secondes
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag pour éviter les boucles infinies lors du rafraîchissement
let isRefreshing = false;
let failedQueue = [];

// Fonction pour traiter la queue des requêtes échouées
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Intercepteur de requête : ajoute le token automatiquement
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    
    // Ajouter le token si disponible (sauf pour les routes d'auth)
    if (token && !config.url?.includes('/auth/login') && !config.url?.includes('/auth/register')) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Si c'est FormData, laisser le navigateur définir Content-Type automatiquement
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur de réponse : gère les erreurs et le rafraîchissement des tokens
apiClient.interceptors.response.use(
  (response) => {
    // Retourner directement les données si la structure est { data: {...} }
    return response.data || response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Si l'erreur est 401 (non autorisé) et qu'on n'est pas déjà en train de rafraîchir
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Si on est sur une route d'auth, ne pas essayer de rafraîchir
      if (originalRequest.url?.includes('/auth/')) {
        return Promise.reject(error);
      }

      // Si on est déjà en train de rafraîchir, mettre en queue
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // Pas de refresh token, déconnecter l'utilisateur
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        processQueue(error, null);
        isRefreshing = false;
        
        // Rediriger vers la page de login si on est dans le navigateur
        if (typeof window !== 'undefined') {
          window.location.href = ROUTES.LOGIN;
        }
        
        return Promise.reject(error);
      }

      try {
        // Essayer de rafraîchir le token
        const response = await axios.post(
          `${ROUTES.API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
          { refreshToken },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const { accessToken, refreshToken: newRefreshToken } = response.data.data || response.data;

        if (accessToken) {
          localStorage.setItem('accessToken', accessToken);
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          // Mettre à jour le header de la requête originale
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;

          // Traiter la queue
          processQueue(null, accessToken);
          isRefreshing = false;

          // Réessayer la requête originale
          return apiClient(originalRequest);
        } else {
          throw new Error('Token de rafraîchissement invalide');
        }
      } catch (refreshError) {
        // Le rafraîchissement a échoué, déconnecter l'utilisateur
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        processQueue(refreshError, null);
        isRefreshing = false;

        // Rediriger vers la page de login
        if (typeof window !== 'undefined') {
          window.location.href = ROUTES.LOGIN;
        }

        return Promise.reject(refreshError);
      }
    }

    // Gestion des autres erreurs
    const errorMessage = error.response?.data?.message || 
                        error.message || 
                        'Une erreur est survenue';

    // Créer une erreur personnalisée avec plus d'informations
    const customError = new Error(errorMessage);
    customError.status = error.response?.status;
    customError.data = error.response?.data;
    customError.isNetworkError = !error.response;

    return Promise.reject(customError);
  }
);

/**
 * Fonction utilitaire pour construire les query params
 */
export const buildQueryParams = (params) => {
  const queryParams = new URLSearchParams();
  
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value.toString());
    }
  });
  
  return queryParams.toString();
};

export default apiClient;

